/* istanbul ignore file */

import PropTypes from 'prop-types'
import {Skeleton} from '@chakra-ui/react'
import React from 'react'
import CMSContent from '../../components/cms-content'
import Section from '../../components/section'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import useInteractionStudioPixel from '../../tracking/interaction-studio'
import Seo from '../../components/seo'
import HeaderContent from '../../components/header-content'
import InteractionStudioContent from '../../components/interaction-studio/content'
import useCmsContent from '../../components/cms-content/hooks/use-cms-content'
import {Redirect, useParams, useLocation} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '../../constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useSlugUrlRedirect from './hooks/use-slug-url-redirect'
import {isServer} from '../../utils/utils'
import LenisProvider from '../../contexts/lenis'
import Chakra3Provider from '../../leap/theme'
import CmsModules from '../../leap/modules'
import {LeapOverlayProvider} from '../../leap/hooks/useOverlay'

const Content = () => {
    const {contentId} = useParams()
    const location = useLocation()

    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        res.set(
            'Cache-Control',
            cacheConfig?.content ||
                `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    useInitialDataLayer({pageType: 'CMS Content'})
    useInteractionStudioPixel()

    const {data: content, isLoading, error} = useCmsContent(contentId)

    if (error) {
        console.error(`Error fetching content id ${contentId}`, error)
        const errorStatus = error?.response?.status || error?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound(`Content Asset "${contentId}" Not Found.`)
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    // redirect to slug url if it's different from the current url
    const redirectTo = useSlugUrlRedirect(isServer, contentId, content)
    if (redirectTo) {
        return <Redirect to={redirectTo} />
    }

    if (!contentId || (!isLoading && !content)) {
        if (res) {
            res.statusCode = 404
        }
        return null
    }

    if (contentId && isLoading) {
        return (
            <Section>
                <Skeleton height="20px" />
            </Section>
        )
    }

    return (
        <>
            {!location.pathname.includes('/stokke-promotions') &&
                !location.pathname.includes('/newsletter') && <HeaderContent />}
            <Seo
                title={content?.pageTitle}
                description={content?.pageDescription}
                keywords={content?.pageKeywords}
            >
                {content?.c_pageType && <meta name="page-type" content={content?.c_pageType} />}
            </Seo>
            <Section>
                {content.c_isLeapContent ? (
                    <Chakra3Provider theme={content.c_colorTheme}>
                        <LenisProvider>
                            <LeapOverlayProvider>
                                <CmsModules
                                    assetId={contentId}
                                    modules={
                                        typeof content.c_body == 'object'
                                            ? content.c_body
                                            : JSON.parse(content.c_body)
                                    }
                                />
                            </LeapOverlayProvider>
                        </LenisProvider>
                    </Chakra3Provider>
                ) : (
                    <InteractionStudioContent id="is-content-content">
                        <CMSContent data-testid="content-page" id={contentId} content={content} />
                    </InteractionStudioContent>
                )}
            </Section>
        </>
    )
}

Content.shouldGetProps = () => false

Content.propTypes = {
    id: PropTypes.string.isRequired
}

export default Content
